import * as React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
// components
import { DrawerModal, VerginaLoader } from 'components/UI'
// constants
import { Modals } from 'constants/modals'
// graphql
import { RISK_TEMPLATE_AI_PROMPTS_QUERY } from 'graphql/risk/queries'
// hooks
import { useModalData } from 'hooks/index'
// themes
import { Header } from 'components/Modals/PlanItems/styles'
import { CloseIcon } from 'components/UI/Icons'
// components
import Thread from './components/Thead'
import Sidebar from './components/Sidebar'
import ChatRuntimeProvider from './ChatRuntimeProvider'
// context
import { ConversationsProvider, useConversations } from './ConversationsContext'
// types
import { AiChatContext } from './types'
// views
import { ModalBody } from './views'
import { EmptyState, ThreadWrapper } from './components/Thead/views'

const PAPER_PROPS = {
  style: {
    height: '100%',
    overflow: 'hidden',
    top: 0
  }
}

interface Props {
  onClose: () => void
  context: AiChatContext
}

const AiChatContent: React.FC<Props> = ({ onClose, context }) => {
  const { loading, currentConversationId, setCurrentConversationId } = useConversations()

  const [sidebarOpen, setSidebarOpen] = React.useState(true)

  const riskTemplateId = context.riskTemplateId

  // Risk chats offer the Summarize button instead of suggested prompts
  const { data } = useQuery<{ riskTemplates: { data: { id: string; aiSuggestedPrompts: string[] }[] } }>(
    RISK_TEMPLATE_AI_PROMPTS_QUERY,
    {
      skip: !riskTemplateId || context.type === 'risk',
      variables: { filter: { ids: [riskTemplateId] } }
    }
  )

  const suggestedPrompts = data?.riskTemplates?.data?.[0]?.aiSuggestedPrompts || []

  // chatKey controls when ChatRuntimeProvider remounts.
  // It only changes on explicit user navigation (new chat / sidebar click),
  // NOT when the backend returns a conversationId after the first message.
  const [chatKey, setChatKey] = React.useState<string | null>(null)

  // Mount the runtime only once the conversation list is known — a risk chat starts
  // by summarising the record, and that decision depends on whether a chat already exists.
  React.useEffect(() => {
    if (loading || chatKey) {
      return
    }

    setChatKey(currentConversationId ?? `new-${Date.now()}`)
  }, [loading, chatKey, currentConversationId])

  const handleNewChat = () => {
    setCurrentConversationId(null)
    setChatKey(`new-${Date.now()}`)
  }

  const handleSelectConversation = (id: string) => {
    setCurrentConversationId(id)
    setChatKey(id)
  }

  return (
    <>
      <Header>
        <span>{`AI Chat — ${context.title}`}</span>
        <CloseIcon onClick={onClose} />
      </Header>
      <ModalBody>
        <Sidebar
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen((o) => !o)}
          onNewChat={handleNewChat}
          onSelect={handleSelectConversation}
        />
        {chatKey ? (
          <ChatRuntimeProvider key={chatKey}>
            <ThreadWrapper>
              <Thread prompts={suggestedPrompts} />
            </ThreadWrapper>
          </ChatRuntimeProvider>
        ) : (
          // The conversation list is still loading — the runtime can't mount until it resolves
          <ThreadWrapper>
            <EmptyState>
              <VerginaLoader size={96} />
            </EmptyState>
          </ThreadWrapper>
        )}
      </ModalBody>
    </>
  )
}

const AiChatModal: React.FC = () => {
  const {
    hideModal,
    modalData: { isOpen, context }
  } = useModalData<{ context?: AiChatContext }>({ modalType: Modals.AI_CHAT_MODAL })

  if (!context) {
    return null
  }

  return (
    <DrawerModal width={'90vw'} isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <ConversationsProvider context={context}>
        <AiChatContent onClose={hideModal} context={context} />
      </ConversationsProvider>
    </DrawerModal>
  )
}

export default AiChatModal
